/**
 * achievementIcons.js — High-Plasticity Canvas 2D icon renderers for
 * the Arsenal "Progress" tab's Erfolge grid and in-game achievement overlays.
 *
 * THEMATIC FRAMEWORK: 9 Distinct Theme Worlds of Deck Invaders
 * 1. 🌿 Sumpf- & Alien-Fauna (Scarabäus, Sumpf-Echse, Krabbler, Späher)
 * 2. 🧬 Sul-Symbiose & Bio-Mutation (Parasitärer Biomorph, Metamorphose-Motte)
 * 3. ⚔️ Taktische Ballistik & Kampf (Glühende Kampfklinge, Hover-Drone-Target, AA Flak-Cannons, Detonation)
 * 4. ⚡ Plasma-Technologie & Reaktor-Flux (Plasma-Flux-Zelle, Chunky Hyper-Overdrive-Vortex)
 * 5. 🛡️ Festungsbau & Krisen-Protokolle (Verbundpanzer-Aegis, Alarm-Turbine, Bunkerplatte)
 * 6. 🌑 Stealth & Void-Schleier (Nebelpirscher, Gekreuzte Schattenklingen)
 * 7. 🌌 Kosmische Phänomene & Zeit-Risse (Feuerball-Komet, Platinum Supernova-X, Warp-Blitz, Eklipse)
 * 8. 💎 Loot, Fugazi & TCG-Kultur (Holo-Goldkarte, 3D-Surge-Walzen, Munitionssafe, Prisma)
 * 9. 🎖️ Flottenränge & Meisterschaft (S-Rank Apex Crest, Sektor-Scan, MVP-Champion, 7-Tage-Protokoll, Apex-Zenit)
 *
 * Each renderer draws into a `size` x `size` canvas starting at (0,0).
 */

const ICON_RENDERERS = {
  // =========================================================================
  // 1. 🌿 SUMPF- & ALIEN-FAUNA
  // =========================================================================

  critterBeetle(ctx, size) {
    const s = size;
    // Ambient emerald bio-glow
    const glow = ctx.createRadialGradient(s * 0.5, s * 0.5, s * 0.1, s * 0.5, s * 0.5, s * 0.45);
    glow.addColorStop(0, 'rgba(16, 185, 129, 0.16)');
    glow.addColorStop(1, 'rgba(16, 185, 129, 0)');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, s, s);

    // 6 Segmented articulated legs with joint bends
    ctx.strokeStyle = '#064e3b';
    ctx.lineWidth = Math.max(1.5, s * 0.038);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    const legOffsets = [[-0.22, -0.05, -0.42, -0.18], [-0.26, 0.08, -0.44, 0.12], [-0.22, 0.22, -0.38, 0.38]];
    for (const [x1, y1, x2, y2] of legOffsets) {
      for (const dir of [-1, 1]) {
        ctx.beginPath();
        ctx.moveTo(s * (0.5 + dir * 0.15), s * (0.52 + y1));
        ctx.lineTo(s * (0.5 + dir * Math.abs(x1)), s * (0.52 + (y1 + y2) * 0.5));
        ctx.lineTo(s * (0.5 + dir * Math.abs(x2)), s * (0.52 + y2));
        ctx.stroke();
      }
    }

    // Curved Scavenger Mandibles
    ctx.strokeStyle = '#f59e0b';
    ctx.lineWidth = Math.max(1.5, s * 0.035);
    for (const dir of [-1, 1]) {
      ctx.beginPath();
      ctx.moveTo(s * (0.5 + dir * 0.08), s * 0.25);
      ctx.quadraticCurveTo(s * (0.5 + dir * 0.2), s * 0.16, s * (0.5 + dir * 0.05), s * 0.12);
      ctx.stroke();
    }

    // Main Carapax (Iridescent Emerald-Jade Body)
    const carapaxGrad = ctx.createRadialGradient(s * 0.42, s * 0.5, s * 0.05, s * 0.5, s * 0.58, s * 0.35);
    carapaxGrad.addColorStop(0, '#6ee7b7');
    carapaxGrad.addColorStop(0.35, '#10b981');
    carapaxGrad.addColorStop(0.75, '#047857');
    carapaxGrad.addColorStop(1, '#064e3b');
    ctx.fillStyle = carapaxGrad;
    ctx.beginPath();
    ctx.ellipse(s * 0.5, s * 0.58, s * 0.28, s * 0.24, 0, 0, Math.PI * 2);
    ctx.fill();

    // Carapax rim highlight
    ctx.strokeStyle = 'rgba(110, 231, 183, 0.6)';
    ctx.lineWidth = Math.max(1, s * 0.02);
    ctx.stroke();

    // Elytron Central Wing Split Line
    ctx.strokeStyle = '#022c22';
    ctx.lineWidth = Math.max(1.5, s * 0.03);
    ctx.beginPath();
    ctx.moveTo(s * 0.5, s * 0.35);
    ctx.lineTo(s * 0.5, s * 0.81);
    ctx.stroke();

    // Specular Wing Gleams
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.45)';
    ctx.lineWidth = Math.max(1, s * 0.022);
    ctx.beginPath();
    ctx.arc(s * 0.42, s * 0.52, s * 0.12, Math.PI * 0.9, Math.PI * 1.4);
    ctx.stroke();

    // Armored Thorax & Head
    const headGrad = ctx.createLinearGradient(0, s * 0.22, 0, s * 0.36);
    headGrad.addColorStop(0, '#1e293b');
    headGrad.addColorStop(1, '#064e3b');
    ctx.fillStyle = headGrad;
    ctx.beginPath();
    ctx.ellipse(s * 0.5, s * 0.32, s * 0.16, s * 0.11, 0, 0, Math.PI * 2);
    ctx.fill();

    // Glowing Amber Eyes
    ctx.fillStyle = '#fbbf24';
    ctx.shadowColor = '#f59e0b';
    ctx.shadowBlur = s * 0.08;
    for (const dir of [-1, 1]) {
      ctx.beginPath();
      ctx.arc(s * (0.5 + dir * 0.12), s * 0.28, s * 0.032, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.shadowBlur = 0;
  },

  critterLizard(ctx, size) {
    const s = size;
    // Ambient cyan bio-glow
    const glow = ctx.createRadialGradient(s * 0.5, s * 0.5, s * 0.1, s * 0.5, s * 0.5, s * 0.45);
    glow.addColorStop(0, 'rgba(6, 182, 212, 0.16)');
    glow.addColorStop(1, 'rgba(6, 182, 212, 0)');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, s, s);

    // Curving tail and spine in teal-cyan gradient
    const spineGrad = ctx.createLinearGradient(s * 0.15, s * 0.8, s * 0.85, s * 0.25);
    spineGrad.addColorStop(0, '#0e7490');
    spineGrad.addColorStop(0.5, '#06b6d4');
    spineGrad.addColorStop(1, '#67e8f9');

    // Sinuous Body
    ctx.strokeStyle = spineGrad;
    ctx.lineWidth = Math.max(3, s * 0.09);
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(s * 0.18, s * 0.82);
    ctx.quadraticCurveTo(s * 0.1, s * 0.55, s * 0.38, s * 0.52);
    ctx.quadraticCurveTo(s * 0.65, s * 0.48, s * 0.72, s * 0.34);
    ctx.stroke();

    // Legs with clawed toes
    ctx.strokeStyle = '#0891b2';
    ctx.lineWidth = Math.max(1.5, s * 0.04);
    ctx.beginPath();
    ctx.moveTo(s * 0.55, s * 0.46);
    ctx.lineTo(s * 0.68, s * 0.6);
    ctx.lineTo(s * 0.78, s * 0.64);
    ctx.moveTo(s * 0.55, s * 0.46);
    ctx.lineTo(s * 0.48, s * 0.32);
    ctx.lineTo(s * 0.42, s * 0.24);
    ctx.moveTo(s * 0.32, s * 0.54);
    ctx.lineTo(s * 0.22, s * 0.68);
    ctx.lineTo(s * 0.16, s * 0.74);
    ctx.stroke();

    // Golden Dorsal Ridges / Spikes
    ctx.fillStyle = '#facc15';
    for (let i = 0; i < 5; i++) {
      const t = i / 4;
      const px = s * (0.32 + t * 0.32);
      const py = s * (0.54 - Math.sin(t * Math.PI) * 0.06);
      ctx.beginPath();
      ctx.moveTo(px, py - s * 0.05);
      ctx.lineTo(px + s * 0.02, py);
      ctx.lineTo(px - s * 0.02, py);
      ctx.closePath();
      ctx.fill();
    }

    // Sculpted Reptile Head
    const headGrad = ctx.createRadialGradient(s * 0.76, s * 0.3, s * 0.02, s * 0.76, s * 0.3, s * 0.12);
    headGrad.addColorStop(0, '#a5f3fc');
    headGrad.addColorStop(0.6, '#06b6d4');
    headGrad.addColorStop(1, '#164e63');
    ctx.fillStyle = headGrad;
    ctx.beginPath();
    ctx.ellipse(s * 0.76, s * 0.3, s * 0.12, s * 0.08, Math.PI * -0.15, 0, Math.PI * 2);
    ctx.fill();

    // Fierce Golden Slit Eye
    ctx.fillStyle = '#fde047';
    ctx.beginPath();
    ctx.arc(s * 0.78, s * 0.28, s * 0.03, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(s * 0.776, s * 0.26, s * 0.008, s * 0.04);
  },

  critterCrab(ctx, size) {
    const s = size;
    // Ambient crimson warm glow
    const glow = ctx.createRadialGradient(s * 0.5, s * 0.5, s * 0.1, s * 0.5, s * 0.5, s * 0.45);
    glow.addColorStop(0, 'rgba(239, 68, 68, 0.16)');
    glow.addColorStop(1, 'rgba(239, 68, 68, 0)');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, s, s);

    // Walking legs (3 on each side)
    ctx.strokeStyle = '#7f1d1d';
    ctx.lineWidth = Math.max(1.5, s * 0.035);
    ctx.lineCap = 'round';
    for (const dir of [-1, 1]) {
      for (let i = 0; i < 3; i++) {
        const baseY = s * (0.55 + i * 0.09);
        ctx.beginPath();
        ctx.moveTo(s * (0.5 + dir * 0.22), baseY);
        ctx.lineTo(s * (0.5 + dir * (0.36 + i * 0.04)), baseY + s * 0.08);
        ctx.lineTo(s * (0.5 + dir * (0.42 + i * 0.06)), baseY + s * 0.22);
        ctx.stroke();
      }
    }

    // Heavy Pincer Claws
    for (const dir of [-1, 1]) {
      ctx.strokeStyle = '#b91c1c';
      ctx.lineWidth = Math.max(2, s * 0.045);
      ctx.beginPath();
      ctx.moveTo(s * (0.5 + dir * 0.18), s * 0.44);
      ctx.lineTo(s * (0.5 + dir * 0.34), s * 0.32);
      ctx.stroke();

      const clawGrad = ctx.createRadialGradient(s * (0.5 + dir * 0.36), s * 0.22, s * 0.02, s * (0.5 + dir * 0.36), s * 0.25, s * 0.12);
      clawGrad.addColorStop(0, '#fca5a5');
      clawGrad.addColorStop(0.5, '#ef4444');
      clawGrad.addColorStop(1, '#991b1b');
      ctx.fillStyle = clawGrad;
      ctx.beginPath();
      ctx.ellipse(s * (0.5 + dir * 0.36), s * 0.24, s * 0.11, s * 0.08, dir * 0.4, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = '#090d16';
      ctx.beginPath();
      ctx.moveTo(s * (0.5 + dir * 0.32), s * 0.22);
      ctx.lineTo(s * (0.5 + dir * 0.42), s * 0.22);
      ctx.lineTo(s * (0.5 + dir * 0.36), s * 0.27);
      ctx.closePath();
      ctx.fill();
    }

    // Armored Main Carapax Shell
    const carapaxGrad = ctx.createRadialGradient(s * 0.46, s * 0.52, s * 0.06, s * 0.5, s * 0.56, s * 0.3);
    carapaxGrad.addColorStop(0, '#f87171');
    carapaxGrad.addColorStop(0.4, '#dc2626');
    carapaxGrad.addColorStop(0.8, '#991b1b');
    carapaxGrad.addColorStop(1, '#450a0a');
    ctx.fillStyle = carapaxGrad;
    ctx.beginPath();
    ctx.ellipse(s * 0.5, s * 0.56, s * 0.27, s * 0.18, 0, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = 'rgba(254, 202, 202, 0.5)';
    ctx.lineWidth = Math.max(1, s * 0.02);
    ctx.stroke();

    // Eye Stalks
    ctx.fillStyle = '#1e293b';
    for (const dir of [-1, 1]) {
      ctx.fillRect(s * (0.5 + dir * 0.1 - 0.015), s * 0.38, s * 0.03, s * 0.06);
      ctx.fillStyle = '#fbbf24';
      ctx.beginPath();
      ctx.arc(s * (0.5 + dir * 0.1), s * 0.38, s * 0.035, 0, Math.PI * 2);
      ctx.fill();
    }
  },

  critterRodent(ctx, size) {
    const s = size;
    // Ambient warm amber glow
    const glow = ctx.createRadialGradient(s * 0.5, s * 0.5, s * 0.1, s * 0.5, s * 0.5, s * 0.45);
    glow.addColorStop(0, 'rgba(245, 158, 11, 0.16)');
    glow.addColorStop(1, 'rgba(245, 158, 11, 0)');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, s, s);

    // Curving dynamic tail with tip glow
    ctx.strokeStyle = '#d97706';
    ctx.lineWidth = Math.max(1.8, s * 0.038);
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(s * 0.22, s * 0.6);
    ctx.quadraticCurveTo(s * 0.04, s * 0.5, s * 0.06, s * 0.28);
    ctx.quadraticCurveTo(s * 0.08, s * 0.15, s * 0.2, s * 0.18);
    ctx.stroke();

    // Sleek Fur Body (Radial bronze-amber gradient)
    const bodyGrad = ctx.createRadialGradient(s * 0.44, s * 0.55, s * 0.05, s * 0.46, s * 0.58, s * 0.28);
    bodyGrad.addColorStop(0, '#fbbf24');
    bodyGrad.addColorStop(0.35, '#d97706');
    bodyGrad.addColorStop(0.75, '#78350f');
    bodyGrad.addColorStop(1, '#451a03');
    ctx.fillStyle = bodyGrad;
    ctx.beginPath();
    ctx.ellipse(s * 0.46, s * 0.58, s * 0.26, s * 0.18, -0.15, 0, Math.PI * 2);
    ctx.fill();

    // Head and Snout
    const headGrad = ctx.createRadialGradient(s * 0.72, s * 0.48, s * 0.02, s * 0.7, s * 0.5, s * 0.16);
    headGrad.addColorStop(0, '#fde68a');
    headGrad.addColorStop(0.5, '#b45309');
    headGrad.addColorStop(1, '#451a03');
    ctx.fillStyle = headGrad;
    ctx.beginPath();
    ctx.ellipse(s * 0.7, s * 0.5, s * 0.15, s * 0.11, -0.2, 0, Math.PI * 2);
    ctx.fill();

    // Ear with pink interior
    ctx.fillStyle = '#78350f';
    ctx.beginPath();
    ctx.ellipse(s * 0.6, s * 0.38, s * 0.06, s * 0.09, 0.3, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#fca5a5';
    ctx.beginPath();
    ctx.ellipse(s * 0.6, s * 0.39, s * 0.03, s * 0.06, 0.3, 0, Math.PI * 2);
    ctx.fill();

    // Paws
    ctx.fillStyle = '#92400e';
    ctx.beginPath();
    ctx.ellipse(s * 0.64, s * 0.72, s * 0.06, s * 0.03, 0, 0, Math.PI * 2);
    ctx.ellipse(s * 0.36, s * 0.72, s * 0.07, s * 0.035, 0, 0, Math.PI * 2);
    ctx.fill();

    // Piercing Amber Cyber-Eye
    ctx.fillStyle = '#facc15';
    ctx.shadowColor = '#f59e0b';
    ctx.shadowBlur = s * 0.06;
    ctx.beginPath();
    ctx.arc(s * 0.76, s * 0.46, s * 0.032, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;
  },

  // =========================================================================
  // 2. 🧬 SUL-SYMBIOSE & BIO-MUTATION
  // =========================================================================

  parasite(ctx, size) {
    const s = size;
    // Ambient bio-crimson haze
    const glow = ctx.createRadialGradient(s * 0.5, s * 0.5, s * 0.1, s * 0.5, s * 0.5, s * 0.48);
    glow.addColorStop(0, 'rgba(225, 29, 72, 0.25)');
    glow.addColorStop(1, 'rgba(225, 29, 72, 0)');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, s, s);

    // Sinister curved bio-talons / tendril legs
    ctx.strokeStyle = '#be123c';
    ctx.lineWidth = Math.max(1.8, s * 0.04);
    ctx.lineCap = 'round';
    for (const dir of [-1, 1]) {
      for (let i = 0; i < 3; i++) {
        const yStart = s * (0.36 + i * 0.14);
        ctx.beginPath();
        ctx.moveTo(s * (0.5 + dir * 0.16), yStart);
        ctx.quadraticCurveTo(s * (0.5 + dir * 0.38), yStart - s * 0.06, s * (0.5 + dir * 0.42), yStart + s * 0.2);
        ctx.stroke();
      }
    }

    // Branching bio-capillaries / veins on background
    ctx.strokeStyle = 'rgba(251, 113, 133, 0.4)';
    ctx.lineWidth = Math.max(1, s * 0.018);
    for (const dir of [-1, 1]) {
      ctx.beginPath();
      ctx.moveTo(s * 0.5, s * 0.5);
      ctx.lineTo(s * (0.5 + dir * 0.18), s * 0.4);
      ctx.lineTo(s * (0.5 + dir * 0.25), s * 0.32);
      ctx.stroke();
    }

    // Visceral Crimson Bio-Node Core
    const coreGrad = ctx.createRadialGradient(s * 0.44, s * 0.44, s * 0.04, s * 0.5, s * 0.5, s * 0.28);
    coreGrad.addColorStop(0, '#fecdd3');
    coreGrad.addColorStop(0.3, '#f43f5e');
    coreGrad.addColorStop(0.7, '#be123c');
    coreGrad.addColorStop(1, '#4c0519');
    ctx.fillStyle = coreGrad;
    ctx.beginPath();
    ctx.arc(s * 0.5, s * 0.5, s * 0.24, 0, Math.PI * 2);
    ctx.fill();

    // Translucent organic membrane gleam
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
    ctx.lineWidth = Math.max(1, s * 0.02);
    ctx.beginPath();
    ctx.arc(s * 0.5, s * 0.5, s * 0.23, Math.PI * 1.1, Math.PI * 1.7);
    ctx.stroke();

    // Sinister Glowing Slit Pupil
    ctx.fillStyle = '#fef08a';
    ctx.shadowColor = '#facc15';
    ctx.shadowBlur = s * 0.08;
    ctx.beginPath();
    ctx.ellipse(s * 0.5, s * 0.48, s * 0.04, s * 0.11, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(s * 0.495, s * 0.42, s * 0.01, s * 0.12);
    ctx.shadowBlur = 0;
  },

  critterMoth(ctx, size) {
    const s = size;
    // Ambient violet/cyan spore cloud
    const glow = ctx.createRadialGradient(s * 0.5, s * 0.5, s * 0.1, s * 0.5, s * 0.5, s * 0.48);
    glow.addColorStop(0, 'rgba(168, 85, 247, 0.22)');
    glow.addColorStop(1, 'rgba(168, 85, 247, 0)');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, s, s);

    // Feathered Antennae
    ctx.strokeStyle = '#c084fc';
    ctx.lineWidth = Math.max(1.2, s * 0.025);
    for (const dir of [-1, 1]) {
      ctx.beginPath();
      ctx.moveTo(s * 0.5, s * 0.32);
      ctx.quadraticCurveTo(s * (0.5 + dir * 0.18), s * 0.18, s * (0.5 + dir * 0.28), s * 0.14);
      ctx.stroke();
    }

    // Broad Velvety Alien Wings
    for (const dir of [-1, 1]) {
      // Upper Wing
      const wingGrad = ctx.createRadialGradient(s * (0.5 + dir * 0.25), s * 0.42, s * 0.05, s * (0.5 + dir * 0.25), s * 0.45, s * 0.3);
      wingGrad.addColorStop(0, '#e0e7ff');
      wingGrad.addColorStop(0.35, '#818cf8');
      wingGrad.addColorStop(0.75, '#4338ca');
      wingGrad.addColorStop(1, '#1e1b4b');
      ctx.fillStyle = wingGrad;
      ctx.beginPath();
      ctx.ellipse(s * (0.5 + dir * 0.24), s * 0.44, s * 0.24, s * 0.16, dir * 0.38, 0, Math.PI * 2);
      ctx.fill();

      // Wing edge border
      ctx.strokeStyle = 'rgba(199, 210, 254, 0.45)';
      ctx.lineWidth = Math.max(1, s * 0.02);
      ctx.stroke();

      // Glowing Bioluminescent Eye-Spots on wings
      ctx.fillStyle = '#22d3ee';
      ctx.shadowColor = '#06b6d4';
      ctx.shadowBlur = s * 0.05;
      ctx.beginPath();
      ctx.arc(s * (0.5 + dir * 0.28), s * 0.44, s * 0.045, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#0f172a';
      ctx.beginPath();
      ctx.arc(s * (0.5 + dir * 0.28), s * 0.44, s * 0.02, 0, Math.PI * 2);
      ctx.fill();
      ctx.shadowBlur = 0;

      // Lower Wing
      ctx.fillStyle = '#312e81';
      ctx.beginPath();
      ctx.ellipse(s * (0.5 + dir * 0.18), s * 0.64, s * 0.16, s * 0.12, dir * -0.25, 0, Math.PI * 2);
      ctx.fill();
    }

    // Soft Thorax & Abdomen
    const bodyGrad = ctx.createLinearGradient(0, s * 0.28, 0, s * 0.76);
    bodyGrad.addColorStop(0, '#f8fafc');
    bodyGrad.addColorStop(0.5, '#64748b');
    bodyGrad.addColorStop(1, '#1e293b');
    ctx.fillStyle = bodyGrad;
    ctx.beginPath();
    ctx.ellipse(s * 0.5, s * 0.52, s * 0.065, s * 0.2, 0, 0, Math.PI * 2);
    ctx.fill();
  },

  // =========================================================================
  // 3. ⚔️ TAKTISCHE BALLISTIK & KAMPF
  // =========================================================================

  combatBlade(ctx, size) {
    const s = size;
    // Muzzle fire / plasma strike glow
    const glow = ctx.createRadialGradient(s * 0.5, s * 0.5, s * 0.1, s * 0.5, s * 0.5, s * 0.48);
    glow.addColorStop(0, 'rgba(249, 115, 22, 0.25)');
    glow.addColorStop(1, 'rgba(249, 115, 22, 0)');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, s, s);

    ctx.save();
    ctx.translate(s * 0.5, s * 0.5);
    ctx.rotate(-Math.PI / 4);

    // Blade Grip & Pommel
    ctx.fillStyle = '#1e293b';
    ctx.fillRect(-s * 0.045, s * 0.16, s * 0.09, s * 0.26);
    ctx.fillStyle = '#f59e0b';
    ctx.fillRect(-s * 0.06, s * 0.4, s * 0.12, s * 0.04);
    ctx.fillRect(-s * 0.07, s * 0.15, s * 0.14, s * 0.03);

    // Superheated Blade Core
    const bladeGrad = ctx.createLinearGradient(-s * 0.08, 0, s * 0.08, 0);
    bladeGrad.addColorStop(0, '#ffffff');
    bladeGrad.addColorStop(0.2, '#fde047');
    bladeGrad.addColorStop(0.5, '#f97316');
    bladeGrad.addColorStop(0.85, '#dc2626');
    bladeGrad.addColorStop(1, '#1e293b');

    ctx.fillStyle = bladeGrad;
    ctx.beginPath();
    ctx.moveTo(0, -s * 0.42);
    ctx.lineTo(s * 0.09, s * 0.14);
    ctx.lineTo(-s * 0.09, s * 0.14);
    ctx.closePath();
    ctx.fill();

    // Edge Specular Gleam
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = Math.max(1.2, s * 0.025);
    ctx.beginPath();
    ctx.moveTo(0, -s * 0.42);
    ctx.lineTo(-s * 0.09, s * 0.14);
    ctx.stroke();

    ctx.restore();

    // Kinetic Impact Sparks
    ctx.fillStyle = '#fef08a';
    const sparks = [[0.65, 0.18], [0.74, 0.26], [0.8, 0.14], [0.6, 0.1]];
    for (const [sx, sy] of sparks) {
      ctx.beginPath();
      ctx.arc(s * sx, s * sy, s * 0.025, 0, Math.PI * 2);
      ctx.fill();
    }
  },

  /**
   * Target Practice — Dedicated Hover Target Drone with concentric bullseye & stabilizer thrusters
   */
  target(ctx, size) {
    const s = size;
    // Ambient tactical amber glow
    const glow = ctx.createRadialGradient(s * 0.5, s * 0.5, s * 0.1, s * 0.5, s * 0.5, s * 0.48);
    glow.addColorStop(0, 'rgba(245, 158, 11, 0.22)');
    glow.addColorStop(1, 'rgba(245, 158, 11, 0)');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, s, s);

    // 4 Diagonal Thruster Pylons (Floating Target Drone)
    ctx.fillStyle = '#334155';
    for (const [dx, dy] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) {
      ctx.beginPath();
      ctx.moveTo(s * 0.5, s * 0.5);
      ctx.lineTo(s * (0.5 + dx * 0.38), s * (0.5 + dy * 0.3));
      ctx.lineTo(s * (0.5 + dx * 0.3), s * (0.5 + dy * 0.38));
      ctx.closePath();
      ctx.fill();

      // Cyan Thruster Flame Exhaust
      ctx.fillStyle = '#38bdf8';
      ctx.beginPath();
      ctx.arc(s * (0.5 + dx * 0.36), s * (0.5 + dy * 0.36), s * 0.045, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#334155';
    }

    // Heavy Circular Drone Armor Chassis
    const armorGrad = ctx.createLinearGradient(0, s * 0.18, 0, s * 0.82);
    armorGrad.addColorStop(0, '#475569');
    armorGrad.addColorStop(0.5, '#1e293b');
    armorGrad.addColorStop(1, '#0f172a');
    ctx.fillStyle = armorGrad;
    ctx.beginPath();
    ctx.arc(s * 0.5, s * 0.5, s * 0.31, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#94a3b8';
    ctx.lineWidth = Math.max(1.5, s * 0.03);
    ctx.stroke();

    // Concentric Bullseye Rings (Bold, High-Contrast)
    // Outer Crimson Band
    ctx.fillStyle = '#ef4444';
    ctx.beginPath();
    ctx.arc(s * 0.5, s * 0.5, s * 0.23, 0, Math.PI * 2);
    ctx.fill();

    // Middle Warning Yellow Ring
    ctx.fillStyle = '#facc15';
    ctx.beginPath();
    ctx.arc(s * 0.5, s * 0.5, s * 0.15, 0, Math.PI * 2);
    ctx.fill();

    // Inner Crimson Core
    ctx.fillStyle = '#dc2626';
    ctx.beginPath();
    ctx.arc(s * 0.5, s * 0.5, s * 0.085, 0, Math.PI * 2);
    ctx.fill();

    // Blinding White Bullseye Center
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(s * 0.5, s * 0.5, s * 0.038, 0, Math.PI * 2);
    ctx.fill();

    // Tactical Target Bracket Reticle (4 corner brackets)
    ctx.strokeStyle = '#38bdf8';
    ctx.lineWidth = Math.max(1.8, s * 0.035);
    const bOffset = s * 0.38;
    const bLen = s * 0.09;
    // Top-left
    ctx.beginPath();
    ctx.moveTo(s * 0.5 - bOffset, s * 0.5 - bOffset + bLen);
    ctx.lineTo(s * 0.5 - bOffset, s * 0.5 - bOffset);
    ctx.lineTo(s * 0.5 - bOffset + bLen, s * 0.5 - bOffset);
    // Top-right
    ctx.moveTo(s * 0.5 + bOffset - bLen, s * 0.5 - bOffset);
    ctx.lineTo(s * 0.5 + bOffset, s * 0.5 - bOffset);
    ctx.lineTo(s * 0.5 + bOffset, s * 0.5 - bOffset + bLen);
    // Bottom-left
    ctx.moveTo(s * 0.5 - bOffset, s * 0.5 + bOffset - bLen);
    ctx.lineTo(s * 0.5 - bOffset, s * 0.5 + bOffset);
    ctx.lineTo(s * 0.5 - bOffset + bLen, s * 0.5 + bOffset);
    // Bottom-right
    ctx.moveTo(s * 0.5 + bOffset - bLen, s * 0.5 + bOffset);
    ctx.lineTo(s * 0.5 + bOffset, s * 0.5 + bOffset);
    ctx.lineTo(s * 0.5 + bOffset, s * 0.5 + bOffset - bLen);
    ctx.stroke();
  },

  /**
   * No-Fly Zone — Heavy Twin Anti-Air Autocannons & Locked Airspace
   */
  noFlyZone(ctx, size) {
    const s = size;
    // Ambient red warning / sky burst glow
    const glow = ctx.createRadialGradient(s * 0.5, s * 0.5, s * 0.1, s * 0.5, s * 0.5, s * 0.48);
    glow.addColorStop(0, 'rgba(239, 68, 68, 0.28)');
    glow.addColorStop(1, 'rgba(239, 68, 68, 0)');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, s, s);

    // Upper Airspace: Struck Enemy Gunship Silhouette
    ctx.fillStyle = '#0f172a';
    ctx.beginPath();
    ctx.moveTo(s * 0.72, s * 0.2);
    ctx.lineTo(s * 0.86, s * 0.14);
    ctx.lineTo(s * 0.8, s * 0.28);
    ctx.lineTo(s * 0.62, s * 0.32);
    ctx.lineTo(s * 0.68, s * 0.24);
    ctx.closePath();
    ctx.fill();

    // Red Lock-On Airspace Hexagon
    ctx.strokeStyle = '#ef4444';
    ctx.lineWidth = Math.max(1.5, s * 0.03);
    ctx.beginPath();
    for (let i = 0; i < 6; i++) {
      const a = (i * Math.PI) / 3;
      const px = s * 0.72 + Math.cos(a) * s * 0.16;
      const py = s * 0.22 + Math.sin(a) * s * 0.16;
      if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
    }
    ctx.closePath();
    ctx.stroke();

    // Smoke trail from struck aircraft
    ctx.strokeStyle = 'rgba(148, 163, 184, 0.45)';
    ctx.lineWidth = Math.max(1, s * 0.02);
    ctx.beginPath();
    ctx.moveTo(s * 0.72, s * 0.22);
    ctx.quadraticCurveTo(s * 0.85, s * 0.3, s * 0.9, s * 0.4);
    ctx.stroke();

    // Lower Turret Chassis & Dual Heavy Flak Barrels (angled up-right 45 deg)
    ctx.save();
    ctx.translate(s * 0.28, s * 0.76);
    ctx.rotate(-Math.PI / 4);

    const barrelGrad = ctx.createLinearGradient(-s * 0.1, 0, s * 0.1, 0);
    barrelGrad.addColorStop(0, '#cbd5e1');
    barrelGrad.addColorStop(0.5, '#475569');
    barrelGrad.addColorStop(1, '#0f172a');
    ctx.fillStyle = barrelGrad;

    // Left Barrel
    ctx.fillRect(-s * 0.08, -s * 0.46, s * 0.065, s * 0.48);
    // Right Barrel
    ctx.fillRect(s * 0.02, -s * 0.46, s * 0.065, s * 0.48);

    // Heavy Muzzle Brakes
    ctx.fillStyle = '#f59e0b';
    ctx.fillRect(-s * 0.09, -s * 0.49, s * 0.085, s * 0.06);
    ctx.fillRect(s * 0.01, -s * 0.49, s * 0.085, s * 0.06);

    // Recoil Mechanism Housing
    ctx.fillStyle = '#334155';
    ctx.fillRect(-s * 0.11, -s * 0.12, s * 0.22, s * 0.22);
    ctx.strokeStyle = '#facc15';
    ctx.lineWidth = Math.max(1.2, s * 0.025);
    ctx.strokeRect(-s * 0.11, -s * 0.12, s * 0.22, s * 0.22);

    ctx.restore();

    // Blazing Double Muzzle Flashes
    const flashCenter = [s * 0.58, s * 0.44];
    const flashGrad = ctx.createRadialGradient(flashCenter[0], flashCenter[1], s * 0.02, flashCenter[0], flashCenter[1], s * 0.14);
    flashGrad.addColorStop(0, '#ffffff');
    flashGrad.addColorStop(0.35, '#fde047');
    flashGrad.addColorStop(0.7, '#f97316');
    flashGrad.addColorStop(1, 'rgba(239, 68, 68, 0)');
    ctx.fillStyle = flashGrad;
    ctx.beginPath();
    ctx.arc(flashCenter[0], flashCenter[1], s * 0.14, 0, Math.PI * 2);
    ctx.fill();

    // Turret Armored Base Plate
    const baseGrad = ctx.createLinearGradient(0, s * 0.72, 0, s * 0.92);
    baseGrad.addColorStop(0, '#475569');
    baseGrad.addColorStop(1, '#0f172a');
    ctx.fillStyle = baseGrad;
    ctx.beginPath();
    ctx.moveTo(s * 0.1, s * 0.92);
    ctx.lineTo(s * 0.2, s * 0.68);
    ctx.lineTo(s * 0.5, s * 0.68);
    ctx.lineTo(s * 0.6, s * 0.92);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = '#94a3b8';
    ctx.lineWidth = Math.max(1.5, s * 0.03);
    ctx.stroke();
  },

  clusterDetonation(ctx, size) {
    const s = size;
    // Central fiery blast glow
    const glow = ctx.createRadialGradient(s * 0.5, s * 0.5, s * 0.05, s * 0.5, s * 0.5, s * 0.48);
    glow.addColorStop(0, '#ffffff');
    glow.addColorStop(0.3, '#f59e0b');
    glow.addColorStop(0.7, '#ef4444');
    glow.addColorStop(1, 'rgba(185, 28, 28, 0)');
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(s * 0.5, s * 0.5, s * 0.44, 0, Math.PI * 2);
    ctx.fill();

    // Shockwave expansion ring
    ctx.strokeStyle = 'rgba(254, 240, 138, 0.8)';
    ctx.lineWidth = Math.max(1.5, s * 0.03);
    ctx.beginPath();
    ctx.arc(s * 0.5, s * 0.5, s * 0.34, 0, Math.PI * 2);
    ctx.stroke();

    // Flying jagged shrapnel fragments
    const fragments = [
      [-0.3, -0.28, 0.08, '#334155'], [0.32, -0.25, 0.09, '#475569'],
      [-0.34, 0.22, 0.08, '#1e293b'], [0.28, 0.3, 0.09, '#334155'],
      [0, -0.38, 0.07, '#64748b'], [0, 0.38, 0.07, '#475569']
    ];
    for (const [dx, dy, sz, col] of fragments) {
      ctx.fillStyle = col;
      ctx.save();
      ctx.translate(s * (0.5 + dx), s * (0.5 + dy));
      ctx.rotate(dx * 4);
      ctx.fillRect(-s * sz * 0.5, -s * sz * 0.5, s * sz, s * sz * 0.6);
      ctx.fillStyle = '#fef08a';
      ctx.fillRect(-s * sz * 0.5, -s * sz * 0.5, s * sz * 0.2, s * sz * 0.6);
      ctx.restore();
    }
  },

  // =========================================================================
  // 4. ⚡ PLASMA-TECHNOLOGIE & REAKTOR-FLUX
  // =========================================================================

  plasmaCore(ctx, size) {
    const s = size;
    // Ambient green plasma glow
    const glow = ctx.createRadialGradient(s * 0.5, s * 0.5, s * 0.1, s * 0.5, s * 0.5, s * 0.48);
    glow.addColorStop(0, 'rgba(34, 197, 94, 0.3)');
    glow.addColorStop(1, 'rgba(34, 197, 94, 0)');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, s, s);

    // Cylinder Glass Tube
    const glassGrad = ctx.createLinearGradient(s * 0.28, 0, s * 0.72, 0);
    glassGrad.addColorStop(0, 'rgba(16, 185, 129, 0.2)');
    glassGrad.addColorStop(0.5, 'rgba(5, 46, 22, 0.6)');
    glassGrad.addColorStop(1, 'rgba(16, 185, 129, 0.2)');
    ctx.fillStyle = glassGrad;
    ctx.fillRect(s * 0.28, s * 0.22, s * 0.44, s * 0.56);

    // Superheated 100% Swirling Green Plasma Core
    const coreGrad = ctx.createLinearGradient(0, s * 0.24, 0, s * 0.76);
    coreGrad.addColorStop(0, '#ecfdf5');
    coreGrad.addColorStop(0.3, '#4ade80');
    coreGrad.addColorStop(0.7, '#10b981');
    coreGrad.addColorStop(1, '#059669');
    ctx.fillStyle = coreGrad;
    ctx.beginPath();
    ctx.ellipse(s * 0.5, s * 0.5, s * 0.15, s * 0.24, 0, 0, Math.PI * 2);
    ctx.fill();

    // Electric arcing filament
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = Math.max(1, s * 0.02);
    ctx.beginPath();
    ctx.moveTo(s * 0.5, s * 0.26);
    ctx.lineTo(s * 0.44, s * 0.4);
    ctx.lineTo(s * 0.56, s * 0.58);
    ctx.lineTo(s * 0.5, s * 0.74);
    ctx.stroke();

    // Top & Bottom Heavy Titanium Caps with Gold Trim
    for (const y of [0.12, 0.78]) {
      ctx.fillStyle = '#334155';
      ctx.fillRect(s * 0.22, s * y, s * 0.56, s * 0.1);
      ctx.fillStyle = '#f59e0b';
      ctx.fillRect(s * 0.24, s * (y === 0.12 ? y + 0.08 : y), s * 0.52, s * 0.02);
      ctx.strokeStyle = '#94a3b8';
      ctx.lineWidth = Math.max(1, s * 0.018);
      ctx.strokeRect(s * 0.22, s * y, s * 0.56, s * 0.1);
    }

    // Side 100% Level Indicator Dots
    ctx.fillStyle = '#4ade80';
    for (let i = 0; i < 4; i++) {
      ctx.beginPath();
      ctx.arc(s * 0.24, s * (0.34 + i * 0.11), s * 0.02, 0, Math.PI * 2);
      ctx.fill();
    }
  },

  /**
   * Hyper Overdrive — Massive, Chunky Industrial Fusion Accelerator Turbine Core
   */
  hyperVortex(ctx, size) {
    const s = size;
    // Ambient intense fusion aura
    const glow = ctx.createRadialGradient(s * 0.5, s * 0.5, s * 0.08, s * 0.5, s * 0.5, s * 0.48);
    glow.addColorStop(0, 'rgba(192, 132, 252, 0.38)');
    glow.addColorStop(0.5, 'rgba(56, 189, 248, 0.2)');
    glow.addColorStop(1, 'rgba(6, 182, 212, 0)');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, s, s);

    // Heavy Outer Overclocked Stator Ring
    const statorGrad = ctx.createLinearGradient(0, s * 0.08, 0, s * 0.92);
    statorGrad.addColorStop(0, '#64748b');
    statorGrad.addColorStop(0.5, '#1e293b');
    statorGrad.addColorStop(1, '#090d16');
    ctx.fillStyle = statorGrad;
    ctx.beginPath();
    ctx.arc(s * 0.5, s * 0.5, s * 0.42, 0, Math.PI * 2);
    ctx.fill();

    // Gold Beveled Stator Flange
    ctx.strokeStyle = '#f59e0b';
    ctx.lineWidth = Math.max(2, s * 0.045);
    ctx.stroke();

    // Recessed Dark Reactor Well
    ctx.fillStyle = '#050811';
    ctx.beginPath();
    ctx.arc(s * 0.5, s * 0.5, s * 0.36, 0, Math.PI * 2);
    ctx.fill();

    // 4 Heavy Curved Overdrive Turbine Blades (Chunky Solid Polygons)
    ctx.save();
    ctx.translate(s * 0.5, s * 0.5);
    for (let i = 0; i < 4; i++) {
      ctx.save();
      ctx.rotate((i * Math.PI) / 2);

      const bladeGrad = ctx.createLinearGradient(0, -s * 0.34, s * 0.28, 0);
      bladeGrad.addColorStop(0, '#c084fc');
      bladeGrad.addColorStop(0.4, '#6366f1');
      bladeGrad.addColorStop(1, '#0f172a');
      ctx.fillStyle = bladeGrad;

      ctx.beginPath();
      ctx.moveTo(s * 0.08, -s * 0.06);
      ctx.quadraticCurveTo(s * 0.28, -s * 0.08, s * 0.34, -s * 0.28);
      ctx.lineTo(s * 0.28, -s * 0.34);
      ctx.quadraticCurveTo(s * 0.16, -s * 0.18, 0, -s * 0.14);
      ctx.closePath();
      ctx.fill();

      // Sharp Neon Glow Edge
      ctx.strokeStyle = '#38bdf8';
      ctx.lineWidth = Math.max(1.5, s * 0.03);
      ctx.stroke();

      ctx.restore();
    }

    // 3 Thick Swirling Plasma Conduits (Massive, vibrant)
    for (let i = 0; i < 3; i++) {
      ctx.save();
      ctx.rotate((i * Math.PI * 2) / 3);
      const conduitGrad = ctx.createLinearGradient(0, 0, s * 0.24, s * 0.16);
      conduitGrad.addColorStop(0, '#ffffff');
      conduitGrad.addColorStop(0.5, i % 2 === 0 ? '#38bdf8' : '#e879f9');
      conduitGrad.addColorStop(1, '#3b82f6');
      ctx.strokeStyle = conduitGrad;
      ctx.lineWidth = Math.max(3.5, s * 0.08); // Chunky, bold!
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.quadraticCurveTo(s * 0.12, -s * 0.1, s * 0.22, s * 0.12);
      ctx.stroke();
      ctx.restore();
    }

    // Blinding Singularity Core with Electric Shockwave
    const core = ctx.createRadialGradient(0, 0, s * 0.02, 0, 0, s * 0.14);
    core.addColorStop(0, '#ffffff');
    core.addColorStop(0.4, '#a5f3fc');
    core.addColorStop(0.7, '#818cf8');
    core.addColorStop(1, 'rgba(99, 102, 241, 0)');
    ctx.fillStyle = core;
    ctx.beginPath();
    ctx.arc(0, 0, s * 0.14, 0, Math.PI * 2);
    ctx.fill();

    // Specular Cross Spark
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(0, 0, s * 0.05, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  },

  // =========================================================================
  // 5. 🛡️ FESTUNGSBAU & KRISEN-PROTOKOLLE
  // =========================================================================

  bastionShield(ctx, size) {
    const s = size;
    // Cyan deflector field glow
    const glow = ctx.createRadialGradient(s * 0.5, s * 0.5, s * 0.1, s * 0.5, s * 0.5, s * 0.48);
    glow.addColorStop(0, 'rgba(14, 165, 233, 0.25)');
    glow.addColorStop(1, 'rgba(14, 165, 233, 0)');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, s, s);

    // Outer Heavy Armor Shield Polygon
    const plateGrad = ctx.createLinearGradient(0, s * 0.14, 0, s * 0.86);
    plateGrad.addColorStop(0, '#475569');
    plateGrad.addColorStop(0.4, '#1e293b');
    plateGrad.addColorStop(1, '#0f172a');
    ctx.fillStyle = plateGrad;

    ctx.beginPath();
    ctx.moveTo(s * 0.5, s * 0.14);
    ctx.lineTo(s * 0.82, s * 0.24);
    ctx.lineTo(s * 0.74, s * 0.65);
    ctx.lineTo(s * 0.5, s * 0.86);
    ctx.lineTo(s * 0.26, s * 0.65);
    ctx.lineTo(s * 0.18, s * 0.24);
    ctx.closePath();
    ctx.fill();

    // Polished Titanium Bevel Edge
    ctx.strokeStyle = '#94a3b8';
    ctx.lineWidth = Math.max(1.8, s * 0.038);
    ctx.stroke();

    // Inner Hexagonal Ion-Deflector Grid
    ctx.strokeStyle = '#38bdf8';
    ctx.lineWidth = Math.max(1, s * 0.02);
    const innerPoints = [
      [0.5, 0.26], [0.7, 0.33], [0.65, 0.58],
      [0.5, 0.74], [0.35, 0.58], [0.3, 0.33]
    ];
    ctx.beginPath();
    innerPoints.forEach(([px, py], i) => {
      if (i === 0) ctx.moveTo(s * px, s * py); else ctx.lineTo(s * px, s * py);
    });
    ctx.closePath();
    ctx.stroke();

    // Central Gold Aegis Chevron
    ctx.fillStyle = '#f59e0b';
    ctx.beginPath();
    ctx.moveTo(s * 0.5, s * 0.38);
    ctx.lineTo(s * 0.58, s * 0.46);
    ctx.lineTo(s * 0.5, s * 0.62);
    ctx.lineTo(s * 0.42, s * 0.46);
    ctx.closePath();
    ctx.fill();

    // Specular highlight
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = Math.max(1, s * 0.02);
    ctx.beginPath();
    ctx.moveTo(s * 0.5, s * 0.16);
    ctx.lineTo(s * 0.8, s * 0.25);
    ctx.stroke();
  },

  phoenixProtocol(ctx, size) {
    const s = size;
    // Emergency Red-Alert Alarm Glow
    const glow = ctx.createRadialGradient(s * 0.5, s * 0.5, s * 0.1, s * 0.5, s * 0.5, s * 0.48);
    glow.addColorStop(0, 'rgba(239, 68, 68, 0.3)');
    glow.addColorStop(1, 'rgba(239, 68, 68, 0)');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, s, s);

    // Circular Heavy Turbine Housing
    const ringGrad = ctx.createLinearGradient(0, s * 0.15, s, s * 0.85);
    ringGrad.addColorStop(0, '#64748b');
    ringGrad.addColorStop(1, '#0f172a');
    ctx.fillStyle = ringGrad;
    ctx.beginPath();
    ctx.arc(s * 0.5, s * 0.5, s * 0.38, 0, Math.PI * 2);
    ctx.fill();

    // Warning Hazard Stripes on Rim
    ctx.strokeStyle = '#f59e0b';
    ctx.lineWidth = Math.max(2, s * 0.04);
    ctx.beginPath();
    ctx.arc(s * 0.5, s * 0.5, s * 0.34, 0, Math.PI * 2);
    ctx.stroke();

    // Recessed turbine well
    ctx.fillStyle = '#050811';
    ctx.beginPath();
    ctx.arc(s * 0.5, s * 0.5, s * 0.28, 0, Math.PI * 2);
    ctx.fill();

    // Red-Hot Emergency Turbine Blades (5 spinning blades)
    for (let i = 0; i < 5; i++) {
      const ang = (i * Math.PI * 2) / 5;
      ctx.save();
      ctx.translate(s * 0.5, s * 0.5);
      ctx.rotate(ang);
      const bladeGrad = ctx.createLinearGradient(0, 0, s * 0.24, 0);
      bladeGrad.addColorStop(0, '#fef08a');
      bladeGrad.addColorStop(0.6, '#ef4444');
      bladeGrad.addColorStop(1, '#7f1d1d');
      ctx.fillStyle = bladeGrad;
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(s * 0.22, -s * 0.06);
      ctx.lineTo(s * 0.26, s * 0.04);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }

    // Flashing Warning Core
    ctx.fillStyle = '#ffffff';
    ctx.shadowColor = '#ef4444';
    ctx.shadowBlur = s * 0.1;
    ctx.beginPath();
    ctx.arc(s * 0.5, s * 0.5, s * 0.08, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;
  },

  ironBunker(ctx, size) {
    const s = size;
    // Tungsten Blast Hatch
    const hatchGrad = ctx.createLinearGradient(s * 0.15, s * 0.15, s * 0.85, s * 0.85);
    hatchGrad.addColorStop(0, '#94a3b8');
    hatchGrad.addColorStop(0.5, '#334155');
    hatchGrad.addColorStop(1, '#0f172a');
    ctx.fillStyle = hatchGrad;

    // Heavy Octagonal Plate
    ctx.beginPath();
    ctx.moveTo(s * 0.3, s * 0.16);
    ctx.lineTo(s * 0.7, s * 0.16);
    ctx.lineTo(s * 0.84, s * 0.3);
    ctx.lineTo(s * 0.84, s * 0.7);
    ctx.lineTo(s * 0.7, s * 0.84);
    ctx.lineTo(s * 0.3, s * 0.84);
    ctx.lineTo(s * 0.16, s * 0.7);
    ctx.lineTo(s * 0.16, s * 0.3);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = '#cbd5e1';
    ctx.lineWidth = Math.max(1.8, s * 0.035);
    ctx.stroke();

    // Criss-Cross Heavy Welded Rebars
    ctx.strokeStyle = '#1e293b';
    ctx.lineWidth = Math.max(2, s * 0.045);
    ctx.beginPath();
    ctx.moveTo(s * 0.22, s * 0.22);
    ctx.lineTo(s * 0.78, s * 0.78);
    ctx.moveTo(s * 0.78, s * 0.22);
    ctx.lineTo(s * 0.22, s * 0.78);
    ctx.stroke();

    // Yellow Radiation / Superweapon Hazard Triangles
    ctx.fillStyle = '#facc15';
    for (let i = 0; i < 3; i++) {
      const ang = (i * Math.PI * 2) / 3 - Math.PI / 2;
      ctx.beginPath();
      ctx.arc(s * 0.5, s * 0.5, s * 0.18, ang - 0.35, ang + 0.35);
      ctx.lineTo(s * 0.5, s * 0.5);
      ctx.closePath();
      ctx.fill();
    }

    // Center Vault Boss
    ctx.fillStyle = '#0f172a';
    ctx.beginPath();
    ctx.arc(s * 0.5, s * 0.5, s * 0.07, 0, Math.PI * 2);
    ctx.fill();
  },

  // =========================================================================
  // 6. 🌑 STEALTH & VOID-SCHLEIER
  // =========================================================================

  fogStalker(ctx, size) {
    const s = size;
    // Stratified Volumetric Fog Layers
    const fogColors = ['rgba(148, 163, 184, 0.18)', 'rgba(56, 189, 248, 0.14)', 'rgba(148, 163, 184, 0.25)'];
    fogColors.forEach((col, idx) => {
      ctx.fillStyle = col;
      const y = s * (0.32 + idx * 0.2);
      ctx.beginPath();
      ctx.ellipse(s * (0.5 + (idx % 2 === 0 ? 0.05 : -0.05)), y, s * (0.38 + idx * 0.05), s * 0.09, 0, 0, Math.PI * 2);
      ctx.fill();
    });

    // Matte-Black Angular Phantom Cowl
    const cowlGrad = ctx.createLinearGradient(0, s * 0.16, 0, s * 0.72);
    cowlGrad.addColorStop(0, '#1e293b');
    cowlGrad.addColorStop(0.6, '#090d16');
    cowlGrad.addColorStop(1, '#020617');
    ctx.fillStyle = cowlGrad;

    ctx.beginPath();
    ctx.moveTo(s * 0.5, s * 0.16);
    ctx.quadraticCurveTo(s * 0.26, s * 0.32, s * 0.28, s * 0.66);
    ctx.lineTo(s * 0.42, s * 0.56);
    ctx.lineTo(s * 0.5, s * 0.68);
    ctx.lineTo(s * 0.58, s * 0.56);
    ctx.lineTo(s * 0.72, s * 0.66);
    ctx.quadraticCurveTo(s * 0.74, s * 0.32, s * 0.5, s * 0.16);
    ctx.closePath();
    ctx.fill();

    // Hood highlight line
    ctx.strokeStyle = 'rgba(148, 163, 184, 0.4)';
    ctx.lineWidth = Math.max(1, s * 0.02);
    ctx.stroke();

    // Steely Dual Thermal Eyes (Red glowing sensors)
    ctx.fillStyle = '#ef4444';
    ctx.shadowColor = '#dc2626';
    ctx.shadowBlur = s * 0.1;
    for (const dir of [-1, 1]) {
      ctx.beginPath();
      ctx.ellipse(s * (0.5 + dir * 0.09), s * 0.42, s * 0.045, s * 0.022, dir * 0.2, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.shadowBlur = 0;
  },

  veilBlade(ctx, size) {
    const s = size;
    // Void mist aura
    const glow = ctx.createRadialGradient(s * 0.5, s * 0.5, s * 0.05, s * 0.5, s * 0.5, s * 0.48);
    glow.addColorStop(0, 'rgba(56, 189, 248, 0.25)');
    glow.addColorStop(1, 'rgba(129, 140, 248, 0)');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, s, s);

    ctx.save();
    ctx.translate(s * 0.5, s * 0.5);

    // Crossing Psionic Void Blades
    for (const ang of [-Math.PI / 4, Math.PI / 4]) {
      ctx.save();
      ctx.rotate(ang);

      const bladeGrad = ctx.createLinearGradient(0, -s * 0.42, 0, s * 0.35);
      bladeGrad.addColorStop(0, '#e0f2fe');
      bladeGrad.addColorStop(0.35, '#38bdf8');
      bladeGrad.addColorStop(0.7, '#6366f1');
      bladeGrad.addColorStop(1, '#090d16');
      ctx.fillStyle = bladeGrad;

      ctx.beginPath();
      ctx.moveTo(0, -s * 0.42);
      ctx.lineTo(s * 0.055, s * 0.16);
      ctx.lineTo(s * 0.02, s * 0.36);
      ctx.lineTo(-s * 0.02, s * 0.36);
      ctx.lineTo(-s * 0.055, s * 0.16);
      ctx.closePath();
      ctx.fill();

      // Guard & Pommel
      ctx.fillStyle = '#f59e0b';
      ctx.fillRect(-s * 0.09, s * 0.16, s * 0.18, s * 0.035);

      ctx.restore();
    }

    // Brilliant Clash Intersection Spark
    ctx.fillStyle = '#ffffff';
    ctx.shadowColor = '#38bdf8';
    ctx.shadowBlur = s * 0.12;
    ctx.beginPath();
    ctx.arc(0, 0, s * 0.06, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;

    ctx.restore();
  },

  // =========================================================================
  // 7. 🌌 KOSMISCHE PHÄNOMENE & ZEIT-RISSE
  // =========================================================================

  /**
   * Streak X (5 Wins) — Blazing Golden Overdrive Meteor Fireball Wedge
   */
  comet(ctx, size) {
    const s = size;
    // Ambient Golden Warm Overdrive Glow
    const glow = ctx.createRadialGradient(s * 0.5, s * 0.5, s * 0.1, s * 0.5, s * 0.5, s * 0.48);
    glow.addColorStop(0, 'rgba(245, 158, 11, 0.25)');
    glow.addColorStop(1, 'rgba(245, 158, 11, 0)');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, s, s);

    // Solid, Massive Golden Meteor Tail Wedge (Chunky polygon)
    const tailGrad = ctx.createLinearGradient(s * 0.1, s * 0.9, s * 0.72, s * 0.25);
    tailGrad.addColorStop(0, 'rgba(153, 27, 27, 0)');
    tailGrad.addColorStop(0.3, '#dc2626');
    tailGrad.addColorStop(0.65, '#f97316');
    tailGrad.addColorStop(0.9, '#fde047');
    tailGrad.addColorStop(1, '#ffffff');
    ctx.fillStyle = tailGrad;

    ctx.beginPath();
    ctx.moveTo(s * 0.08, s * 0.92);
    ctx.lineTo(s * 0.58, s * 0.14);
    ctx.lineTo(s * 0.78, s * 0.22);
    ctx.lineTo(s * 0.86, s * 0.42);
    ctx.closePath();
    ctx.fill();

    // Inner White-Hot Plasma Spine
    const spineGrad = ctx.createLinearGradient(s * 0.22, s * 0.78, s * 0.74, s * 0.24);
    spineGrad.addColorStop(0, 'rgba(254, 240, 138, 0)');
    spineGrad.addColorStop(0.5, '#fef08a');
    spineGrad.addColorStop(1, '#ffffff');
    ctx.strokeStyle = spineGrad;
    ctx.lineWidth = Math.max(2.5, s * 0.06);
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(s * 0.22, s * 0.78);
    ctx.lineTo(s * 0.74, s * 0.24);
    ctx.stroke();

    // Heavy Molten Meteorite Nucleus
    const rockGrad = ctx.createRadialGradient(s * 0.72, s * 0.26, s * 0.03, s * 0.72, s * 0.26, s * 0.18);
    rockGrad.addColorStop(0, '#ffffff');
    rockGrad.addColorStop(0.4, '#fde047');
    rockGrad.addColorStop(0.7, '#ea580c');
    rockGrad.addColorStop(1, '#78350f');
    ctx.fillStyle = rockGrad;
    ctx.beginPath();
    ctx.arc(s * 0.72, s * 0.26, s * 0.16, 0, Math.PI * 2);
    ctx.fill();

    // Shockwave Ring around head
    ctx.strokeStyle = '#fef08a';
    ctx.lineWidth = Math.max(1.8, s * 0.035);
    ctx.beginPath();
    ctx.arc(s * 0.72, s * 0.26, s * 0.21, -Math.PI * 0.8, Math.PI * 0.3);
    ctx.stroke();
  },

  /**
   * Streak X-Treme (10 Wins) — Massive Supercharged Platinum "X" Nova
   */
  twinMeteor(ctx, size) {
    const s = size;
    // Ambient Electric Platinum Supernova Glow
    const glow = ctx.createRadialGradient(s * 0.5, s * 0.5, s * 0.08, s * 0.5, s * 0.5, s * 0.48);
    glow.addColorStop(0, 'rgba(56, 189, 248, 0.35)');
    glow.addColorStop(0.5, 'rgba(99, 102, 241, 0.2)');
    glow.addColorStop(1, 'rgba(56, 189, 248, 0)');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, s, s);

    ctx.save();
    ctx.translate(s * 0.5, s * 0.5);

    // Chunky, Massive Platinum "X" Hyper-Beams
    for (const angle of [-Math.PI / 4, Math.PI / 4]) {
      ctx.save();
      ctx.rotate(angle);

      // Outer Cyan Energy Beam
      const beamGrad = ctx.createLinearGradient(0, -s * 0.44, 0, s * 0.44);
      beamGrad.addColorStop(0, '#ffffff');
      beamGrad.addColorStop(0.2, '#38bdf8');
      beamGrad.addColorStop(0.5, '#4338ca');
      beamGrad.addColorStop(0.8, '#38bdf8');
      beamGrad.addColorStop(1, '#ffffff');
      ctx.strokeStyle = beamGrad;
      ctx.lineWidth = Math.max(5, s * 0.13); // Bold & chunky!
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(0, -s * 0.42);
      ctx.lineTo(0, s * 0.42);
      ctx.stroke();

      // Inner White-Hot Core Ray
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = Math.max(2, s * 0.045);
      ctx.beginPath();
      ctx.moveTo(0, -s * 0.42);
      ctx.lineTo(0, s * 0.42);
      ctx.stroke();

      ctx.restore();
    }

    // 4 Symmetrical Radiant Diamond Shockwave Flares at endpoints
    ctx.fillStyle = '#ffffff';
    for (const [dx, dy] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) {
      ctx.beginPath();
      ctx.arc(s * dx * 0.3, s * dy * 0.3, s * 0.045, 0, Math.PI * 2);
      ctx.fill();
    }

    // Central Epicenter Nova Explosion (Faceted Diamond Core)
    const novaGrad = ctx.createRadialGradient(0, 0, s * 0.02, 0, 0, s * 0.18);
    novaGrad.addColorStop(0, '#ffffff');
    novaGrad.addColorStop(0.4, '#e0f2fe');
    novaGrad.addColorStop(0.7, '#38bdf8');
    novaGrad.addColorStop(1, 'rgba(56, 189, 248, 0)');
    ctx.fillStyle = novaGrad;
    ctx.beginPath();
    ctx.arc(0, 0, s * 0.18, 0, Math.PI * 2);
    ctx.fill();

    // Central Diamond Glyph
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.moveTo(0, -s * 0.1);
    ctx.lineTo(s * 0.1, 0);
    ctx.lineTo(0, s * 0.1);
    ctx.lineTo(-s * 0.1, 0);
    ctx.closePath();
    ctx.fill();

    ctx.restore();
  },

  lightningBlitz(ctx, size) {
    const s = size;
    // Ambient electric cyan warp flash
    const glow = ctx.createRadialGradient(s * 0.5, s * 0.5, s * 0.1, s * 0.5, s * 0.5, s * 0.48);
    glow.addColorStop(0, 'rgba(56, 189, 248, 0.3)');
    glow.addColorStop(1, 'rgba(56, 189, 248, 0)');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, s, s);

    // Fractured Stopwatch Chronograph Ring
    ctx.strokeStyle = 'rgba(148, 163, 184, 0.5)';
    ctx.lineWidth = Math.max(1.5, s * 0.035);
    ctx.beginPath();
    ctx.arc(s * 0.5, s * 0.5, s * 0.36, 0, Math.PI * 1.8);
    ctx.stroke();

    // Jagged Speed Lightning Bolt
    const boltGrad = ctx.createLinearGradient(s * 0.65, s * 0.12, s * 0.35, s * 0.88);
    boltGrad.addColorStop(0, '#fef08a');
    boltGrad.addColorStop(0.5, '#38bdf8');
    boltGrad.addColorStop(1, '#ffffff');
    ctx.fillStyle = boltGrad;

    ctx.beginPath();
    ctx.moveTo(s * 0.56, s * 0.14);
    ctx.lineTo(s * 0.32, s * 0.48);
    ctx.lineTo(s * 0.52, s * 0.48);
    ctx.lineTo(s * 0.4, s * 0.86);
    ctx.lineTo(s * 0.72, s * 0.42);
    ctx.lineTo(s * 0.52, s * 0.42);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = Math.max(1, s * 0.02);
    ctx.stroke();
  },

  moon(ctx, size) {
    const s = size;
    // Atmospheric Blue Eclipse Corona
    const corona = ctx.createRadialGradient(s * 0.5, s * 0.5, s * 0.28, s * 0.5, s * 0.5, s * 0.46);
    corona.addColorStop(0, 'rgba(56, 189, 248, 0.3)');
    corona.addColorStop(1, 'rgba(56, 189, 248, 0)');
    ctx.fillStyle = corona;
    ctx.beginPath();
    ctx.arc(s * 0.5, s * 0.5, s * 0.45, 0, Math.PI * 2);
    ctx.fill();

    // Dark side of the moon (spherical shading)
    const darkSide = ctx.createRadialGradient(s * 0.42, s * 0.46, s * 0.08, s * 0.5, s * 0.5, s * 0.36);
    darkSide.addColorStop(0, '#1e293b');
    darkSide.addColorStop(1, '#0b1220');
    ctx.fillStyle = darkSide;
    ctx.beginPath();
    ctx.arc(s * 0.5, s * 0.5, s * 0.36, 0, Math.PI * 2);
    ctx.fill();

    // Glowing Silver Crescent
    const moonGrad = ctx.createLinearGradient(s * 0.15, s * 0.15, s * 0.7, s * 0.7);
    moonGrad.addColorStop(0, '#ffffff');
    moonGrad.addColorStop(0.5, '#e2e8f0');
    moonGrad.addColorStop(1, '#94a3b8');
    ctx.fillStyle = moonGrad;

    ctx.beginPath();
    ctx.arc(s * 0.5, s * 0.5, s * 0.36, -Math.PI * 0.45, Math.PI * 0.65, false);
    ctx.arc(s * 0.62, s * 0.38, s * 0.31, Math.PI * 0.65, -Math.PI * 0.45, true);
    ctx.closePath();
    ctx.fill();

    // Crater reliefs
    ctx.fillStyle = 'rgba(100, 116, 139, 0.45)';
    ctx.beginPath();
    ctx.arc(s * 0.36, s * 0.55, s * 0.045, 0, Math.PI * 2);
    ctx.arc(s * 0.32, s * 0.42, s * 0.03, 0, Math.PI * 2);
    ctx.arc(s * 0.45, s * 0.68, s * 0.035, 0, Math.PI * 2);
    ctx.fill();
  },

  // =========================================================================
  // 8. 💎 LOOT, FUGAZI & TCG-KULTUR
  // =========================================================================

  goldCard(ctx, size) {
    const s = size;
    // Warm Gold Aura
    const glow = ctx.createRadialGradient(s * 0.5, s * 0.5, s * 0.1, s * 0.5, s * 0.5, s * 0.48);
    glow.addColorStop(0, 'rgba(250, 204, 21, 0.25)');
    glow.addColorStop(1, 'rgba(250, 204, 21, 0)');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, s, s);

    // Outer Beveled Imperial Gold Frame
    const goldBorder = ctx.createLinearGradient(s * 0.24, s * 0.12, s * 0.76, s * 0.88);
    goldBorder.addColorStop(0, '#fef08a');
    goldBorder.addColorStop(0.4, '#f59e0b');
    goldBorder.addColorStop(0.8, '#b45309');
    goldBorder.addColorStop(1, '#78350f');
    ctx.fillStyle = goldBorder;

    ctx.beginPath();
    ctx.moveTo(s * 0.3, s * 0.12);
    ctx.lineTo(s * 0.7, s * 0.12);
    ctx.lineTo(s * 0.74, s * 0.16);
    ctx.lineTo(s * 0.74, s * 0.84);
    ctx.lineTo(s * 0.7, s * 0.88);
    ctx.lineTo(s * 0.3, s * 0.88);
    ctx.lineTo(s * 0.26, s * 0.84);
    ctx.lineTo(s * 0.26, s * 0.16);
    ctx.closePath();
    ctx.fill();

    // Inner Holographic Card Window
    const holoGrad = ctx.createLinearGradient(s * 0.3, s * 0.18, s * 0.7, s * 0.82);
    holoGrad.addColorStop(0, '#fde68a');
    holoGrad.addColorStop(0.3, '#38bdf8');
    holoGrad.addColorStop(0.6, '#f472b6');
    holoGrad.addColorStop(1, '#fbbf24');
    ctx.fillStyle = holoGrad;
    ctx.fillRect(s * 0.31, s * 0.18, s * 0.38, s * 0.64);

    // Inner Card Artwork Shadow
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(s * 0.34, s * 0.22, s * 0.32, s * 0.34);

    // Embossed Imperial Star on Card
    ctx.fillStyle = '#fde047';
    const cx = s * 0.5, cy = s * 0.39, r = s * 0.1;
    ctx.beginPath();
    for (let i = 0; i < 5; i++) {
      const a = -Math.PI / 2 + (i * 4 * Math.PI) / 5;
      const px = cx + Math.cos(a) * r, py = cy + Math.sin(a) * r;
      if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
    }
    ctx.closePath();
    ctx.fill();

    // Specular Diagonal Light Sweep
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.7)';
    ctx.lineWidth = Math.max(1.5, s * 0.03);
    ctx.beginPath();
    ctx.moveTo(s * 0.28, s * 0.55);
    ctx.lineTo(s * 0.55, s * 0.14);
    ctx.stroke();
  },

  slotReel(ctx, size) {
    const s = size;
    // Mechanical Slot Drum Chassis
    const casingGrad = ctx.createLinearGradient(0, s * 0.18, 0, s * 0.82);
    casingGrad.addColorStop(0, '#475569');
    casingGrad.addColorStop(0.5, '#1e293b');
    casingGrad.addColorStop(1, '#090d16');
    ctx.fillStyle = casingGrad;
    ctx.fillRect(s * 0.15, s * 0.2, s * 0.7, s * 0.6);

    // Brass Gear Flanges on Sides
    ctx.fillStyle = '#d97706';
    ctx.fillRect(s * 0.12, s * 0.32, s * 0.04, s * 0.36);
    ctx.fillRect(s * 0.84, s * 0.32, s * 0.04, s * 0.36);

    // 3 Visible 3D Reels
    const reelW = s * 0.2;
    for (let i = 0; i < 3; i++) {
      const rx = s * (0.18 + i * 0.22);
      const cylGrad = ctx.createLinearGradient(0, s * 0.24, 0, s * 0.76);
      cylGrad.addColorStop(0, '#0f172a');
      cylGrad.addColorStop(0.3, '#fef08a');
      cylGrad.addColorStop(0.5, '#f59e0b');
      cylGrad.addColorStop(0.7, '#78350f');
      cylGrad.addColorStop(1, '#0f172a');
      ctx.fillStyle = cylGrad;
      ctx.fillRect(rx, s * 0.24, reelW, s * 0.52);

      ctx.fillStyle = '#050811';
      ctx.fillRect(rx + reelW - s * 0.02, s * 0.24, s * 0.02, s * 0.52);
    }

    // Horizontal Winning Payline Bar
    ctx.fillStyle = 'rgba(56, 189, 248, 0.4)';
    ctx.fillRect(s * 0.15, s * 0.46, s * 0.7, s * 0.08);
    ctx.strokeStyle = '#38bdf8';
    ctx.lineWidth = Math.max(1, s * 0.02);
    ctx.strokeRect(s * 0.15, s * 0.46, s * 0.7, s * 0.08);
  },

  chest(ctx, size) {
    const s = size;
    // Gold Loot Haze
    const glow = ctx.createRadialGradient(s * 0.5, s * 0.5, s * 0.1, s * 0.5, s * 0.5, s * 0.48);
    glow.addColorStop(0, 'rgba(245, 158, 11, 0.2)');
    glow.addColorStop(1, 'rgba(245, 158, 11, 0)');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, s, s);

    // Sci-Fi Armored Cargo Vault Body
    const vaultGrad = ctx.createLinearGradient(0, s * 0.38, 0, s * 0.84);
    vaultGrad.addColorStop(0, '#475569');
    vaultGrad.addColorStop(0.6, '#1e293b');
    vaultGrad.addColorStop(1, '#090d16');
    ctx.fillStyle = vaultGrad;
    ctx.fillRect(s * 0.16, s * 0.42, s * 0.68, s * 0.4);

    // Heavy Reinforced Vault Lid
    const lidGrad = ctx.createLinearGradient(0, s * 0.22, 0, s * 0.42);
    lidGrad.addColorStop(0, '#94a3b8');
    lidGrad.addColorStop(0.5, '#475569');
    lidGrad.addColorStop(1, '#1e293b');
    ctx.fillStyle = lidGrad;
    ctx.beginPath();
    ctx.ellipse(s * 0.5, s * 0.42, s * 0.34, s * 0.18, 0, Math.PI, 0);
    ctx.fill();

    // Imperial Gold Band Clamps
    ctx.fillStyle = '#f59e0b';
    ctx.fillRect(s * 0.16, s * 0.4, s * 0.68, s * 0.05);
    ctx.fillRect(s * 0.28, s * 0.3, s * 0.05, s * 0.52);
    ctx.fillRect(s * 0.67, s * 0.3, s * 0.05, s * 0.52);

    // High-Security Electronic Lock Dial
    ctx.fillStyle = '#0f172a';
    ctx.beginPath();
    ctx.arc(s * 0.5, s * 0.58, s * 0.08, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#facc15';
    ctx.lineWidth = Math.max(1.5, s * 0.03);
    ctx.stroke();

    // Glowing Emerald Access LED
    ctx.fillStyle = '#22c55e';
    ctx.shadowColor = '#4ade80';
    ctx.shadowBlur = s * 0.06;
    ctx.beginPath();
    ctx.arc(s * 0.5, s * 0.58, s * 0.035, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;
  },

  palette(ctx, size) {
    const s = size;
    // Faceted Optical Crystal Prism
    ctx.save();
    ctx.translate(s * 0.5, s * 0.5);

    // Incident White Light Beam
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.lineWidth = Math.max(1.5, s * 0.035);
    ctx.beginPath();
    ctx.moveTo(-s * 0.44, 0);
    ctx.lineTo(-s * 0.12, 0);
    ctx.stroke();

    // Refracted Rainbow Spectrum Fan
    const rainbow = ['#ef4444', '#f97316', '#facc15', '#22c55e', '#38bdf8', '#a855f7'];
    rainbow.forEach((color, i) => {
      const spread = (i - 2.5) * 0.08;
      ctx.strokeStyle = color;
      ctx.lineWidth = Math.max(1.5, s * 0.03);
      ctx.beginPath();
      ctx.moveTo(s * 0.08, spread * s * 0.3);
      ctx.lineTo(s * 0.44, spread * s * 1.1);
      ctx.stroke();
    });

    // 3D Glass Prism Triangle
    const prismGrad = ctx.createLinearGradient(-s * 0.18, -s * 0.28, s * 0.18, s * 0.28);
    prismGrad.addColorStop(0, 'rgba(255, 255, 255, 0.6)');
    prismGrad.addColorStop(0.5, 'rgba(186, 230, 253, 0.3)');
    prismGrad.addColorStop(1, 'rgba(14, 165, 233, 0.5)');
    ctx.fillStyle = prismGrad;

    ctx.beginPath();
    ctx.moveTo(0, -s * 0.32);
    ctx.lineTo(s * 0.24, s * 0.22);
    ctx.lineTo(-s * 0.24, s * 0.22);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = Math.max(1, s * 0.02);
    ctx.stroke();

    ctx.restore();
  },

  // =========================================================================
  // 9. 🎖️ FLOTTENRÄNGE & MEISTERSCHAFT
  // =========================================================================

  territorialRadar(ctx, size) {
    const s = size;
    // Radar Screen Base
    const screenGrad = ctx.createRadialGradient(s * 0.5, s * 0.5, s * 0.05, s * 0.5, s * 0.5, s * 0.42);
    screenGrad.addColorStop(0, '#064e3b');
    screenGrad.addColorStop(0.6, '#022c22');
    screenGrad.addColorStop(1, '#050811');
    ctx.fillStyle = screenGrad;
    ctx.beginPath();
    ctx.arc(s * 0.5, s * 0.5, s * 0.4, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = '#059669';
    ctx.lineWidth = Math.max(1.5, s * 0.03);
    ctx.stroke();

    // 65%+ Dominated Hexagon Sector Mesh
    ctx.fillStyle = 'rgba(34, 197, 94, 0.35)';
    ctx.strokeStyle = '#22c55e';
    ctx.lineWidth = Math.max(1, s * 0.02);
    const hexes = [
      [0.45, 0.35], [0.6, 0.4], [0.5, 0.5], [0.65, 0.55], [0.4, 0.55], [0.55, 0.7]
    ];
    hexes.forEach(([hx, hy]) => {
      ctx.beginPath();
      for (let i = 0; i < 6; i++) {
        const a = (i * Math.PI) / 3;
        const px = s * (hx + Math.cos(a) * 0.09);
        const py = s * (hy + Math.sin(a) * 0.09);
        if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
      }
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    });

    // Sweeping Green Radar Line
    const sweepGrad = ctx.createLinearGradient(s * 0.5, s * 0.5, s * 0.85, s * 0.2);
    sweepGrad.addColorStop(0, '#ffffff');
    sweepGrad.addColorStop(1, '#22c55e');
    ctx.strokeStyle = sweepGrad;
    ctx.lineWidth = Math.max(1.8, s * 0.035);
    ctx.beginPath();
    ctx.moveTo(s * 0.5, s * 0.5);
    ctx.lineTo(s * 0.82, s * 0.22);
    ctx.stroke();
  },

  sRankCrest(ctx, size) {
    const s = size;
    // Gold Imperial Wings Glory
    const glow = ctx.createRadialGradient(s * 0.5, s * 0.5, s * 0.1, s * 0.5, s * 0.5, s * 0.48);
    glow.addColorStop(0, 'rgba(250, 204, 21, 0.3)');
    glow.addColorStop(1, 'rgba(250, 204, 21, 0)');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, s, s);

    // Spreading Faceted Gold Wings
    for (const dir of [-1, 1]) {
      const wingGrad = ctx.createLinearGradient(s * 0.5, s * 0.5, s * (0.5 + dir * 0.4), s * 0.2);
      wingGrad.addColorStop(0, '#fef08a');
      wingGrad.addColorStop(0.5, '#f59e0b');
      wingGrad.addColorStop(1, '#78350f');
      ctx.fillStyle = wingGrad;

      ctx.beginPath();
      ctx.moveTo(s * 0.5, s * 0.55);
      ctx.lineTo(s * (0.5 + dir * 0.44), s * 0.28);
      ctx.lineTo(s * (0.5 + dir * 0.36), s * 0.46);
      ctx.lineTo(s * (0.5 + dir * 0.42), s * 0.62);
      ctx.lineTo(s * 0.5, s * 0.74);
      ctx.closePath();
      ctx.fill();
    }

    // Central Shield Medallion
    const wellGrad = ctx.createRadialGradient(s * 0.5, s * 0.5, s * 0.04, s * 0.5, s * 0.5, s * 0.25);
    wellGrad.addColorStop(0, '#1e3a8a');
    wellGrad.addColorStop(0.8, '#0f172a');
    wellGrad.addColorStop(1, '#050811');
    ctx.fillStyle = wellGrad;
    ctx.beginPath();
    ctx.arc(s * 0.5, s * 0.52, s * 0.22, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = '#facc15';
    ctx.lineWidth = Math.max(1.8, s * 0.038);
    ctx.stroke();

    // Embossed 3D "S" Letter
    ctx.fillStyle = '#ffffff';
    ctx.font = `900 ${Math.round(s * 0.32)}px 'Orbitron', sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.shadowColor = '#facc15';
    ctx.shadowBlur = s * 0.08;
    ctx.fillText('S', s * 0.5, s * 0.54);
    ctx.shadowBlur = 0;
  },

  unitMvp(ctx, size) {
    const s = size;
    // Commendation Medallion Outer Octagon
    const medalGrad = ctx.createLinearGradient(0, s * 0.15, 0, s * 0.85);
    medalGrad.addColorStop(0, '#fdba74');
    medalGrad.addColorStop(0.5, '#b45309');
    medalGrad.addColorStop(1, '#78350f');
    ctx.fillStyle = medalGrad;

    ctx.beginPath();
    ctx.moveTo(s * 0.32, s * 0.16);
    ctx.lineTo(s * 0.68, s * 0.16);
    ctx.lineTo(s * 0.82, s * 0.32);
    ctx.lineTo(s * 0.82, s * 0.68);
    ctx.lineTo(s * 0.68, s * 0.84);
    ctx.lineTo(s * 0.32, s * 0.84);
    ctx.lineTo(s * 0.18, s * 0.68);
    ctx.lineTo(s * 0.18, s * 0.32);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = '#fde68a';
    ctx.lineWidth = Math.max(1.5, s * 0.03);
    ctx.stroke();

    // Inner Pilot Helmet Visor Profile
    ctx.fillStyle = '#0f172a';
    ctx.beginPath();
    ctx.arc(s * 0.5, s * 0.44, s * 0.18, 0, Math.PI * 2);
    ctx.fill();

    // Gold Visor Glow
    ctx.fillStyle = '#38bdf8';
    ctx.beginPath();
    ctx.ellipse(s * 0.52, s * 0.44, s * 0.12, s * 0.06, 0.1, 0, Math.PI * 2);
    ctx.fill();

    // Raised 5-Point Honor Star
    ctx.fillStyle = '#facc15';
    const cx = s * 0.5, cy = s * 0.7, r = s * 0.11;
    ctx.beginPath();
    for (let i = 0; i < 5; i++) {
      const a = -Math.PI / 2 + (i * 4 * Math.PI) / 5;
      const px = cx + Math.cos(a) * r, py = cy + Math.sin(a) * r;
      if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
    }
    ctx.closePath();
    ctx.fill();
  },

  /**
   * Daily Protocol — Tactical 7-Day Protocol Verification Console (Replaces Moon)
   */
  dailyProtocol(ctx, size) {
    const s = size;
    // Ambient emerald protocol verification glow
    const glow = ctx.createRadialGradient(s * 0.5, s * 0.5, s * 0.1, s * 0.5, s * 0.5, s * 0.48);
    glow.addColorStop(0, 'rgba(34, 197, 94, 0.25)');
    glow.addColorStop(1, 'rgba(34, 197, 94, 0)');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, s, s);

    // Heavy Circular Console Chassis in Gunmetal & Titanium
    const chassisGrad = ctx.createLinearGradient(0, s * 0.1, 0, s * 0.9);
    chassisGrad.addColorStop(0, '#475569');
    chassisGrad.addColorStop(0.5, '#1e293b');
    chassisGrad.addColorStop(1, '#090d16');
    ctx.fillStyle = chassisGrad;
    ctx.beginPath();
    ctx.arc(s * 0.5, s * 0.5, s * 0.42, 0, Math.PI * 2);
    ctx.fill();

    // Outer Imperial Gold Calibration Rim
    ctx.strokeStyle = '#f59e0b';
    ctx.lineWidth = Math.max(1.8, s * 0.038);
    ctx.stroke();

    // 7 Perimeter Status Pips / Day Trackers (7 Days of Protocol)
    for (let i = 0; i < 7; i++) {
      const ang = (i * Math.PI * 2) / 7 - Math.PI / 2;
      const px = s * 0.5 + Math.cos(ang) * s * 0.36;
      const py = s * 0.5 + Math.sin(ang) * s * 0.36;

      ctx.fillStyle = (i === 6) ? '#fde047' : '#22c55e'; // 7th day jackpot milestone!
      ctx.beginPath();
      ctx.arc(px, py, s * 0.032, 0, Math.PI * 2);
      ctx.fill();

      ctx.strokeStyle = '#050811';
      ctx.lineWidth = Math.max(0.8, s * 0.015);
      ctx.stroke();
    }

    // Inner Recessed Terminal Datapad Display
    ctx.fillStyle = '#050811';
    ctx.beginPath();
    ctx.arc(s * 0.5, s * 0.5, s * 0.28, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = 'rgba(34, 197, 94, 0.35)';
    ctx.lineWidth = Math.max(1, s * 0.02);
    ctx.stroke();

    // 3 Completed Daily Quest Protocol Check Bars
    const checkY = [0.38, 0.5, 0.62];
    checkY.forEach((yFrac) => {
      // Horizontal Task Bar
      ctx.fillStyle = '#1e293b';
      ctx.fillRect(s * 0.32, s * (yFrac - 0.035), s * 0.36, s * 0.07);

      // Glowing Green Verification Checkmark
      ctx.strokeStyle = '#4ade80';
      ctx.lineWidth = Math.max(1.5, s * 0.03);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(s * 0.36, s * yFrac);
      ctx.lineTo(s * 0.41, s * (yFrac + 0.025));
      ctx.lineTo(s * 0.49, s * (yFrac - 0.025));
      ctx.stroke();

      // Progress Fill Stripe
      ctx.fillStyle = '#22c55e';
      ctx.fillRect(s * 0.53, s * (yFrac - 0.02), s * 0.12, s * 0.04);
    });
  },

  shatter(ctx, size) {
    const s = size;
    // Void Prism Explosion (Void Destroyer)
    const glow = ctx.createRadialGradient(s * 0.5, s * 0.5, s * 0.05, s * 0.5, s * 0.5, s * 0.48);
    glow.addColorStop(0, 'rgba(168, 85, 247, 0.25)');
    glow.addColorStop(1, 'rgba(56, 189, 248, 0)');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, s, s);

    const shardColors = ['#e0e7ff', '#38bdf8', '#818cf8', '#c084fc', '#4f46e5', '#0284c7'];
    let seed = 42;
    const rand = () => { seed = (seed * 1103515245 + 12345) & 0x7fffffff; return (seed % 1000) / 1000; };

    for (let i = 0; i < 10; i++) {
      const ang = (i / 10) * Math.PI * 2 + rand() * 0.25;
      const dist = s * (0.14 + rand() * 0.28);
      const cx = s * 0.5 + Math.cos(ang) * dist;
      const cy = s * 0.5 + Math.sin(ang) * dist;
      const r = s * (0.05 + rand() * 0.055);

      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(ang + rand());
      ctx.fillStyle = shardColors[i % shardColors.length];
      ctx.beginPath();
      ctx.moveTo(-r, -r * 0.7);
      ctx.lineTo(r * 1.2, 0);
      ctx.lineTo(0, r * 1.3);
      ctx.closePath();
      ctx.fill();

      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = Math.max(0.8, s * 0.015);
      ctx.stroke();
      ctx.restore();
    }
  },

  peak(ctx, size) {
    const s = size;
    // Apex Celestial Harmony (Orbital Apex Level 55 / 1000 Wins)
    // 1. Lunar Crescent Base
    ICON_RENDERERS.moon(ctx, size);

    // 2. Rising Ascending Radiant Comet
    const cometGrad = ctx.createLinearGradient(s * 0.2, s * 0.75, s * 0.78, s * 0.25);
    cometGrad.addColorStop(0, 'rgba(56, 189, 248, 0)');
    cometGrad.addColorStop(0.7, 'rgba(56, 189, 248, 0.8)');
    cometGrad.addColorStop(1, '#ffffff');
    ctx.strokeStyle = cometGrad;
    ctx.lineWidth = s * 0.08;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(s * 0.22, s * 0.72);
    ctx.lineTo(s * 0.74, s * 0.24);
    ctx.stroke();

    // 3. Multi-Faceted 8-Pointed Apex Star at the Summit
    ctx.fillStyle = '#ffffff';
    ctx.shadowColor = '#facc15';
    ctx.shadowBlur = s * 0.12;
    const cx = s * 0.76, cy = s * 0.22;
    ctx.beginPath();
    for (let i = 0; i < 8; i++) {
      const a = (i * Math.PI) / 4;
      const r = (i % 2 === 0) ? s * 0.12 : s * 0.045;
      const px = cx + Math.cos(a) * r, py = cy + Math.sin(a) * r;
      if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
    }
    ctx.closePath();
    ctx.fill();
    ctx.shadowBlur = 0;
  }
};

export const ACHIEVEMENT_ICON_IDS = Object.freeze(Object.keys(ICON_RENDERERS));

const iconBitmapCache = new Map();

/**
 * Renders one achievement icon to an offscreen canvas once and caches it -
 * same "bake once, blit forever" approach as buildSpriteBitmap()
 * (js/engine/render/unitSprites.js).
 */
export function buildAchievementIconBitmap(iconId, size = 64) {
  const key = `${iconId}_${size}`;
  const cached = iconBitmapCache.get(key);
  if (cached) return cached;

  // No DOM (Node test runs): hand back an inert stand-in instead of throwing.
  if (typeof document === 'undefined') return { toDataURL: () => '', width: size, height: size };

  const renderer = ICON_RENDERERS[iconId] || ICON_RENDERERS.target;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  renderer(ctx, size);

  iconBitmapCache.set(key, canvas);
  return canvas;
}
